/**
 * Rituals in Aujourd'hui (13/10/2026): a recurring moment of work held by one
 * person. Its occurrence is the last scheduled day up to today; not done, it
 * stays until the next one replaces it, so nothing piles up.
 */

export type RitualRhythm = 'weekly' | 'monthly';

export interface WorkspaceRitual {
  id: string;
  tenant_id: string;
  owner_user_id: string;
  created_by: string;
  title: string;
  detail: string | null;
  rhythm: RitualRhythm;
  /** ISO weekdays, 1 = Monday … 7 = Sunday; one for a monthly ritual. */
  days: number[];
  route: string | null;
  source_usage_id: string | null;
  active: boolean;
  created_at: string;
  updated_at: string;
}

/** One occurrence done, with what the person declared (both optional). */
export interface RitualOccurrence {
  ritual_id: string;
  occurs_on: string;
  done_by: string;
  done_at: string;
  note: string | null;
  decision: string | null;
}

export const WEEKDAY_LABELS: Record<number, { short: string; long: string }> = {
  1: { short: 'L', long: 'lundi' },
  2: { short: 'M', long: 'mardi' },
  3: { short: 'M', long: 'mercredi' },
  4: { short: 'J', long: 'jeudi' },
  5: { short: 'V', long: 'vendredi' },
  6: { short: 'S', long: 'samedi' },
  7: { short: 'D', long: 'dimanche' },
};

const DAY_MS = 86_400_000;
/** Calendar arithmetic on YYYY-MM-DD at noon UTC: no daylight-saving edge. */
const parse = (iso: string): Date => new Date(`${iso}T12:00:00Z`);
const format = (date: Date): string => date.toISOString().slice(0, 10);
const isoWeekday = (date: Date): number => ((date.getUTCDay() + 6) % 7) + 1;

/** The first given weekday of the month of `date`. */
const firstWeekdayOfMonth = (date: Date, weekday: number): Date => {
  const first = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1, 12));
  return new Date(first.getTime() + ((weekday - isoWeekday(first) + 7) % 7) * DAY_MS);
};

/** The last scheduled day on or before `today` (YYYY-MM-DD); null before the first one. */
export const lastOccurrence = (ritual: Pick<WorkspaceRitual, 'rhythm' | 'days' | 'created_at'>, today: string): string | null => {
  const day = parse(today);
  const since = ritual.created_at.slice(0, 10);
  let found: Date | null = null;
  if (ritual.rhythm === 'weekly') {
    for (let back = 0; back < 7 && !found; back += 1) {
      const candidate = new Date(day.getTime() - back * DAY_MS);
      if (ritual.days.includes(isoWeekday(candidate))) found = candidate;
    }
  } else {
    const weekday = ritual.days[0] ?? 1;
    const thisMonth = firstWeekdayOfMonth(day, weekday);
    found = thisMonth <= day ? thisMonth : firstWeekdayOfMonth(new Date(Date.UTC(day.getUTCFullYear(), day.getUTCMonth() - 1, 1, 12)), weekday);
  }
  // A ritual added today starts with its next occurrence, unless today is one.
  return found && format(found) >= since ? format(found) : null;
};

/** « chaque lundi », « du lundi au vendredi », « le premier lundi du mois ». */
export const describeRhythm = (ritual: Pick<WorkspaceRitual, 'rhythm' | 'days'>): string => {
  const days = [...ritual.days].sort();
  if (ritual.rhythm === 'monthly') return `le premier ${WEEKDAY_LABELS[days[0] ?? 1]!.long} du mois`;
  if (days.length === 7) return 'chaque jour';
  if (days.join() === '1,2,3,4,5') return 'du lundi au vendredi';
  const names = days.map((entry) => WEEKDAY_LABELS[entry]!.long);
  return names.length === 1 ? `chaque ${names[0]}` : `chaque ${names.slice(0, -1).join(', ')} et ${names.at(-1)}`;
};
